/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { FC, useState, useEffect } from "react";
import { styled, lighten, useTheme } from "@mui/material/styles";
import { Box } from "@mui/material";
import { ReactComponent as BatteryShapeSvg } from "assets/battery_shape.svg";
import { ReactComponent as BatteryAmountSvg } from "assets/battery_amount.svg";
import { ReactComponent as BatteryOfflineSvg } from "assets/battery_offline.svg";
import { ReactComponent as BatteryChargeSvg } from "assets/battery_charge.svg";
import { AwvInfo, AwvInfoConditionEnum } from "client";

interface BatteryProps {
  awvInfo: AwvInfo;
}

export const Battery: FC<BatteryProps> = ({ awvInfo }) => {
  const [amountWidth, setAmountWidth] = useState<number>(0);
  const [amountColor, setAmountColor] = useState<string>("transparent");
  const theme = useTheme();

  useEffect(() => {
    const newAmount = awvInfo.soc ?? 0;
    setAmountWidth(Math.ceil((newAmount * 36) / 100));
    if (awvInfo.condition === AwvInfoConditionEnum.Charging) {
      setAmountColor(lighten(theme.palette.success.main, 0.5));
    } else if (newAmount > 20) {
      setAmountColor("#707070");
    } else if (newAmount > 0) {
      setAmountColor(theme.palette.error.main);
    } else {
      setAmountColor("transparent");
    }
  }, [awvInfo.condition, awvInfo.soc, theme]);

  return (
    <BatteryContainer>
      <BatteryShapeSvg />
      {awvInfo.condition &&
        awvInfo.condition !== AwvInfoConditionEnum.Offline && (
          <BatteryAmount barwidth={amountWidth} barcolor={amountColor} />
        )}
      {(!awvInfo.condition ||
        awvInfo.condition === AwvInfoConditionEnum.Offline) && (
        <BatteryOfflineSvg />
      )}
      {awvInfo.condition === AwvInfoConditionEnum.Charging && (
        <BatteryChargeSvg />
      )}
      <NumberValue>
        {awvInfo.soc ?? "--"}
        <span>%</span>
      </NumberValue>
    </BatteryContainer>
  );
};

const BatteryContainer = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  display: "grid",
  gridTemplateColumns: "40px 48px",
  marginRight: theme.spacing(1),
  padding: theme.spacing(0),
  svg: {
    gridColumn: 1,
    gridRow: 1,
  },
}));

interface BatteryAmountProps {
  barwidth: number;
  barcolor: string;
}

const BatteryAmount = styled(BatteryAmountSvg)<BatteryAmountProps>(
  ({ barwidth, barcolor }) => ({
    "& .battery-amount": {
      width: `${barwidth}px`,
      fill: barcolor,
    },
  })
);

const NumberValue = styled(Box)(({ theme }) => ({
  gridColumn: 2,
  gridRow: 1,
  display: "flex",
  flexDirection: "row",
  alignItems: "baseline",
  justifyContent: "flex-end",
  fontSize: theme.typography.h6.fontSize,
  fontWeight: "normal",
  span: {
    marginLeft: 1,
    fontSize: "80%",
  },
}));
